import { useEffect } from 'react'
import { useQueryClient, type QueryKey } from '@tanstack/react-query'
import {
  notificationContactsApi, camerasApi, systemSettingsApi, userApi, pushPreferencesApi,
} from '@/api/endpoints'
import { queryKeys, queryOptions } from '@/config/queryConfig'

// ============================================================================
// Item 307 — the Settings queries, one definition each, shared by the sections that read them and by the
// background prefetch of the tabs that are not open. The functions call the API lazily (arrow functions),
// so a test that mocks only part of the endpoints module can still import this file.
// ============================================================================

export type SettingsTab = 'notifications' | 'sounds' | 'security' | 'system'

export const contactsQuery = {
  queryKey: ['notification-contacts'] as const,
  queryFn: () => notificationContactsApi.getAll(),
}

/** The camera list under the app's shared key (WebSocketProvider already keeps it warm), not Settings' own copy. */
export const camerasQuery = {
  queryKey: queryKeys.cameras.all(),
  queryFn: () => camerasApi.getAll(),
  staleTime: queryOptions.cameras.all.staleTime,
}

/** #323 — a super admin's push list is EVERY camera (GET /cameras/?scope=all, #095), whose rows carry push_enabled,
 *  push_filters and is_owner since #323. Its own key: the app's shared owned+shared list must not be replaced by
 *  admin-shaped rows. */
export const adminCamerasQuery = {
  queryKey: queryKeys.cameras.pushSettingsAdmin(),
  queryFn: () => camerasApi.getAllAdmin(),
  staleTime: queryOptions.cameras.all.staleTime,
}

export const defaultEventsViewQuery = {
  queryKey: queryKeys.systemSettings.defaultEventsView(),
  queryFn: () => systemSettingsApi.getGlobalDefaultEventsView(),
  ...queryOptions.systemSettings.defaultEventsView,
}

export const pushPreferencesQuery = {
  queryKey: ['push-preferences'] as const,
  queryFn: () => pushPreferencesApi.get(),
}

export const pushCatalogQuery = {
  queryKey: ['push-preferences-catalog'] as const,
  queryFn: () => pushPreferencesApi.catalog(),
}

export const profileQuery = {
  queryKey: ['user', 'profile'] as const,
  queryFn: () => userApi.getProfile(),
}

/** #294 redesign — the super admin's critical sound rules: the same key, function and options as the sound
 *  engine's useCriticalSoundSettings, so the System tab, the Sounds & display tab and every dashboard read
 *  one cache. */
export const criticalSoundQuery = {
  queryKey: queryKeys.systemSettings.criticalSound(),
  queryFn: () => systemSettingsApi.getCriticalSound(),
  ...queryOptions.systemSettings.criticalSound,
}

export const alertLinkExpiryQuery = {
  queryKey: ['system-settings', 'alert-link-expiry'] as const,
  queryFn: () => systemSettingsApi.getAlertLinkExpiry(),
}

interface SettingsQuery {
  queryKey: QueryKey
  queryFn: () => Promise<unknown>
  staleTime?: number
}

/** What each tab's sections ask the server for. A section added to a tab adds its query here. */
export function settingsTabQueries(tab: SettingsTab, isSuperuser: boolean): SettingsQuery[] {
  switch (tab) {
    case 'notifications':
      return [
        contactsQuery,
        ...(isSuperuser ? [defaultEventsViewQuery] : []),
        camerasQuery,
        ...(isSuperuser ? [adminCamerasQuery] : []),
        pushPreferencesQuery,
        pushCatalogQuery,
      ]
    case 'sounds':
      // the switches and sliders are stored in the browser; the critical sound's Play reads the super
      // admin's sound rules, which every dashboard already keeps fresh (no extra request)
      return [criticalSoundQuery]
    case 'security':
      return [profileQuery]
    case 'system':
      return [criticalSoundQuery, alertLinkExpiryQuery]
  }
}

/** If the open tab's data has not settled by then, the other tabs are fetched anyway. */
export const PREFETCH_FALLBACK_MS = 1000

/**
 * The open tab loads first; once its queries have settled (or after PREFETCH_FALLBACK_MS) the other visible
 * tabs' queries are prefetched in the background, so switching tabs shows data at once. Data still fresh in
 * the cache is not fetched again (the client's staleTime).
 */
export function usePrefetchOtherTabs(active: SettingsTab, tabs: SettingsTab[], isSuperuser: boolean) {
  const queryClient = useQueryClient()
  const tabsKey = tabs.join(',')

  useEffect(() => {
    const visible = tabsKey.split(',') as SettingsTab[]
    const own = settingsTabQueries(active, isSuperuser)
    const others = visible.filter((tab) => tab !== active).flatMap((tab) => settingsTabQueries(tab, isSuperuser))
    if (others.length === 0) return

    let started = false
    const start = () => {
      if (started) return
      started = true
      cleanup()
      for (const q of others) void queryClient.prefetchQuery(q)
    }
    const settled = () => own.every((q) => {
      const state = queryClient.getQueryState(q.queryKey)
      return !!state && state.status !== 'pending' && state.fetchStatus === 'idle'
    })

    const timer = setTimeout(start, PREFETCH_FALLBACK_MS)
    const unsubscribe = queryClient.getQueryCache().subscribe(() => { if (settled()) start() })
    function cleanup() {
      clearTimeout(timer)
      unsubscribe()
    }
    if (settled()) start()

    return () => {
      started = true
      cleanup()
    }
  }, [active, tabsKey, isSuperuser, queryClient])
}
